import { useState, useEffect, useRef, useCallback } from "react";

// ─── Minimal GIF89a Encoder (pure JS, no deps) ───
class GIFEncoder {
  constructor(w, h) {
    this.width = w;
    this.height = h;
    this.frames = [];
    this.delay = 80;
  }
  setDelay(ms) { this.delay = ms; }
  addFrame(ctx) {
    const imgData = ctx.getImageData(0, 0, this.width, this.height);
    this.frames.push(imgData);
  }
  encode() {
    const { width, height, frames, delay } = this;
    if (!frames.length) return null;
    const colorMap = new Map();
    const palette = [];
    const indexedFrames = [];
    for (const frame of frames) {
      const indices = new Uint8Array(width * height);
      for (let i = 0; i < frame.data.length; i += 4) {
        const r = frame.data[i], g = frame.data[i+1], b = frame.data[i+2];
        const qr = (r >> 3) << 3, qg = (g >> 3) << 3, qb = (b >> 3) << 3;
        const key = (qr << 16) | (qg << 8) | qb;
        let idx;
        if (colorMap.has(key)) { idx = colorMap.get(key); }
        else {
          idx = palette.length;
          if (idx < 256) { palette.push([qr, qg, qb]); colorMap.set(key, idx); }
          else {
            let best = Infinity, bi = 0;
            for (let j = 0; j < palette.length; j++) {
              const dr = palette[j][0]-qr, dg = palette[j][1]-qg, db = palette[j][2]-qb;
              const d = dr*dr+dg*dg+db*db;
              if (d < best) { best = d; bi = j; }
            }
            idx = bi;
          }
        }
        indices[i / 4] = idx;
      }
      indexedFrames.push(indices);
    }
    while (palette.length < 256) palette.push([0, 0, 0]);
    const out = [];
    const w8 = (b) => out.push(b & 0xFF);
    const w16 = (v) => { w8(v); w8(v >> 8); };
    const ws = (s) => { for (let i = 0; i < s.length; i++) w8(s.charCodeAt(i)); };
    ws("GIF89a"); w16(width); w16(height);
    w8(0xF7); w8(0); w8(0);
    for (let i = 0; i < 256; i++) { w8(palette[i][0]); w8(palette[i][1]); w8(palette[i][2]); }
    w8(0x21); w8(0xFF); w8(11); ws("NETSCAPE2.0"); w8(3); w8(1); w16(0); w8(0);
    for (let f = 0; f < indexedFrames.length; f++) {
      w8(0x21); w8(0xF9); w8(4); w8(0x04);
      w16(Math.round(delay / 10)); w8(0); w8(0);
      w8(0x2C); w16(0); w16(0); w16(width); w16(height); w8(0);
      const minCode = 8; w8(minCode);
      const lzw = this._lzw(indexedFrames[f], minCode);
      let pos = 0;
      while (pos < lzw.length) {
        const chunk = Math.min(255, lzw.length - pos);
        w8(chunk);
        for (let i = 0; i < chunk; i++) w8(lzw[pos++]);
      }
      w8(0);
    }
    w8(0x3B);
    return new Uint8Array(out);
  }
  _lzw(indices, minCodeSize) {
    const clearCode = 1 << minCodeSize;
    const eoiCode = clearCode + 1;
    let codeSize = minCodeSize + 1;
    let nextCode = eoiCode + 1;
    const table = new Map();
    const init = () => {
      table.clear();
      for (let i = 0; i < clearCode; i++) table.set(String(i), i);
      codeSize = minCodeSize + 1;
      nextCode = eoiCode + 1;
    };
    const output = [];
    let buffer = 0, bufLen = 0;
    const emit = (code) => {
      buffer |= (code << bufLen); bufLen += codeSize;
      while (bufLen >= 8) { output.push(buffer & 0xFF); buffer >>= 8; bufLen -= 8; }
    };
    init(); emit(clearCode);
    let cur = String(indices[0]);
    for (let i = 1; i < indices.length; i++) {
      const next = String(indices[i]);
      const combined = cur + "," + next;
      if (table.has(combined)) { cur = combined; }
      else {
        emit(table.get(cur));
        if (nextCode < 4096) {
          table.set(combined, nextCode++);
          if (nextCode > (1 << codeSize) && codeSize < 12) codeSize++;
        } else { emit(clearCode); init(); }
        cur = next;
      }
    }
    emit(table.get(cur));
    emit(eoiCode);
    if (bufLen > 0) output.push(buffer & 0xFF);
    return output;
  }
}

// ─── Normies On-Chain Palette ───
const C = {
  on: "#48494b",
  off: "#e3e5e4",
  bg: "#f2f2f0",
  white: "#ffffff",
  mid: "#8e8f91",
  faint: "#c8c9cb",
  divider: "#d5d6d8",
};

// ─── Additional Colors for Splash ───
const COLORS = [
  "#FF5733", "#33FF57", "#3357FF", "#F333FF", "#FF33A1",
  "#33FFF3", "#FFC733", "#A133FF", "#FF8333", "#33FF83"
];

// ─── Meme Templates by Sentiment ───
const MEMES = {
  pump: [
    { top: "FLOOR IS PUMPING", bottom: "AND I'M STILL NOT SELLING" },
    { top: "PAPER HANDS CRYING RN", bottom: "DIAMOND HANDS DINING" },
    { top: "WE'RE ALL GONNA MAKE IT", bottom: "" },
    { top: "MOM I'M A GENIUS", bottom: "I BOUGHT THE DIP" },
    { top: "BEARS IN SHAMBLES", bottom: "NORMIES ON TOP" },
    { top: "WHO SOLD AT THE BOTTOM", bottom: "SHOW YOURSELF" },
    { top: "JUST CALLED MY BOSS", bottom: "I DON'T WORK THERE ANYMORE" },
    { top: "GREEN CANDLES EVERYWHERE", bottom: "TODAY WE FEAST" },
    { top: "UP ONLY", bottom: "THIS IS THE WAY" },
    { top: "NORMIES PUMPING", bottom: "AS EXPECTED" },
  ],
  dump: [
    { top: "IT'S JUST A DIP", bottom: "HE SAID FOR THE 47TH TIME" },
    { top: "DOWN BAD", bottom: "BUT STILL HOLDING" },
    { top: "FLOOR IS GONE", bottom: "JUST LIKE MY SAVINGS" },
    { top: "MOM COME PICK ME UP", bottom: "THE MARKET IS SCARY" },
    { top: "THIS IS FINE", bottom: "" },
    { top: "BOUGHT THE DIP", bottom: "BUT IT KEPT DIPPING" },
    { top: "ZOOM OUT THEY SAID", bottom: "IT'S WORSE ZOOMED OUT" },
    { top: "SER THE FLOOR", bottom: "IT'S IN THE BASEMENT" },
    { top: "RAMEN DIET DAY 14", bottom: "" },
    { top: "PAIN", bottom: "" },
  ],
  crab: [
    { top: "DOING ABSOLUTELY NOTHING", bottom: "JUST LIKE THE FLOOR" },
    { top: "DAY 47 OF CRAB MARKET", bottom: "STILL VIBING" },
    { top: "NOBODY IS SELLING", bottom: "NOBODY IS BUYING EITHER" },
    { top: "TOUCH GRASS?", bottom: "NAH I'LL STARE AT OPENSEA" },
    { top: "FLOOR HASN'T MOVED", bottom: "NEITHER HAVE I" },
    { top: "REFRESHING OPENSEA", bottom: "FOR THE 200TH TIME TODAY" },
    { top: "PATIENCE", bottom: "IS A VIRTUE" },
    { top: "STILL EARLY SER", bottom: "" },
  ],
  generic: [
    { top: "GM", bottom: "FROM YOUR LOCAL NORMIE" },
    { top: "NGMI", bottom: "IF YOU DON'T OWN A NORMIE" },
    { top: "IMAGINE NOT OWNING", bottom: "A 40x40 MASTERPIECE" },
    { top: "ONCHAIN FOREVER", bottom: "" },
    { top: "MY PFP > YOUR PFP", bottom: "" },
    { top: "NORMIE GANG", bottom: "40x40 PIXELS OF GLORY" },
    { top: "CT BE LIKE", bottom: "NICE PFP ANON" },
    { top: "ART", bottom: "" },
  ],
};

const API = "https://api.normies.art/normie";

export default function NormiesMemeGen() {
  const [normieId, setNormieId] = useState(null);
  const [pixels, setPixels] = useState(null);
  const [traits, setTraits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [memeText, setMemeText] = useState({ top: "", bottom: "" });
  const [customTop, setCustomTop] = useState("");
  const [customBottom, setCustomBottom] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [floorPrice, setFloorPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(null);
  const [sentiment, setSentiment] = useState("generic");
  const [showWatermark, setShowWatermark] = useState(true);
  const [animating, setAnimating] = useState(false);
  const [gifProgress, setGifProgress] = useState(null);
  const [blockHash, setBlockHash] = useState(null);
  const [harmonicParams, setHarmonicParams] = useState(null);
  const [showHarmonics, setShowHarmonics] = useState(false);
  const [colorSplash, setColorSplash] = useState(false);
  const [animationStyle, setAnimationStyle] = useState('breathe');
  const [animSpeed, setAnimSpeed] = useState(1);
  const [manualPrice, setManualPrice] = useState("");
  const [manualChange, setManualChange] = useState("");
  const [pixelCount, setPixelCount] = useState(0);
  const [idInput, setIdInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const frameRef = useRef(0);
  const particlesRef = useRef([]);

  // ─── Block Hash Generator ───
  const generateBlockHash = useCallback(() => {
    const t = Date.now();
    let hash = "";
    for (let i = 0; i < 64; i++) hash += ((t * (i + 7) * 31 + i * 17) % 16).toString(16);
    setBlockHash(hash);
    const b = [];
    for (let i = 0; i < hash.length; i += 2) b.push(parseInt(hash.substr(i, 2), 16));
    setHarmonicParams({
      freqA: 1 + (b[0] % 5), freqB: 1 + (b[1] % 5),
      phase: (b[2] / 255) * Math.PI * 2,
      amplitude: 0.28 + (b[3] / 255) * 0.35,
      decay: 0.002 + (b[4] / 255) * 0.004,
      rotation: (b[5] / 255) * Math.PI * 2,
      complexity: 2 + (b[6] % 4),
    });
    return hash;
  }, []);

  const getSentiment = useCallback((c) => {
    if (c === null || c === undefined) return "generic";
    if (c > 5) return "pump"; if (c < -5) return "dump"; return "crab";
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://api.opensea.io/api/v2/collections/normies/stats", { headers: { accept: "application/json" } });
        if (res.ok) {
          const d = await res.json();
          const f = d?.total?.floor_price || d?.stats?.floor_price;
          const c = d?.total?.floor_price_one_day_change_percent ?? null;
          if (f) { setFloorPrice(parseFloat(f)); setPriceChange(c !== null ? parseFloat(c) : null); setSentiment(getSentiment(c !== null ? parseFloat(c) : null)); }
        }
      } catch(e) {}
    })();
    generateBlockHash();
  }, [getSentiment, generateBlockHash]);

  const applyManualPrice = () => {
    const p = parseFloat(manualPrice), c = parseFloat(manualChange);
    if (!isNaN(p)) { setFloorPrice(p); setPriceChange(!isNaN(c)?c:null); setSentiment(getSentiment(!isNaN(c)?c:null)); }
  };

  // ─── Load Normie ───
  const loadNormie = async (specificId) => {
    setLoading(true); setAnimating(false);
    if (animRef.current) cancelAnimationFrame(animRef.current);
    frameRef.current = 0;
    particlesRef.current = [];
    const id = specificId !== undefined ? specificId : Math.floor(Math.random() * 10000);
    setNormieId(id); generateBlockHash();
    try {
      const pixRes = await fetch(`${API}/${id}/pixels`);
      if (pixRes.ok) {
        const pixStr = await pixRes.text();
        const grid = []; let onC = 0;
        for (let y = 0; y < 40; y++) {
          const row = [];
          for (let x = 0; x < 40; x++) { const v = pixStr[y*40+x]==="1"?1:0; if(v)onC++; row.push(v); }
          grid.push(row);
        }
        setPixels(grid); setPixelCount(onC);
      }
      const trRes = await fetch(`${API}/${id}/traits`);
      if (trRes.ok) { const tr = await trRes.json(); setTraits(tr.attributes || []); }
    } catch(e) {}
    if (!isCustom) {
      const t = MEMES[sentiment] || MEMES.generic;
      setMemeText(t[Math.floor(Math.random()*t.length)]);
    }
    setLoading(false); setAnimating(true);
  };

  const shuffleMeme = () => {
    const t = MEMES[sentiment] || MEMES.generic;
    setMemeText(t[Math.floor(Math.random()*t.length)]); setIsCustom(false);
  };

  const applyCustom = () => {
    if (customTop||customBottom) {
      setMemeText({ top: customTop.toUpperCase()||memeText.top, bottom: customBottom.toUpperCase()||memeText.bottom });
      setIsCustom(true);
    }
  };

  const initParticles = useCallback((style, pixels) => {
    const parts = [];
    const cx = 20, cy = 20;
    for (let y = 0; y < 40; y++) {
      for (let x = 0; x < 40; x++) {
        if (pixels[y][x]) {
          if (style === 'explode') {
            const dx = x - cx, dy = y - cy;
            const angle = Math.atan2(dy, dx);
            const dist = Math.sqrt(dx*dx + dy*dy);
            const speed = 0.1 + dist * 0.01;
            parts.push({
              x, y,
              vx: Math.cos(angle) * speed,
              vy: Math.sin(angle) * speed,
              life: 1,
              vlife: -0.005
            });
          } else if (style === 'melt') {
            parts.push({
              x, y,
              vy: Math.random() * 0.2 + 0.1,
              life: 1,
              vlife: 0
            });
          } else if (style === 'evaporate') {
            parts.push({
              x, y,
              life: Math.random(),
              vlife: -0.005 - Math.random() * 0.01
            });
          }
        }
      }
    }
    particlesRef.current = parts;
  }, []);

  useEffect(() => {
    if (pixels && ['explode', 'melt', 'evaporate'].includes(animationStyle)) {
      initParticles(animationStyle, pixels);
    }
  }, [animationStyle, pixels, initParticles]);

  // ─── Canvas Draw ───
  const drawFrame = useCallback((ctx, W, H, frame, isStatic) => {
    const t = frame * 0.04 * animSpeed;
    // Background
    ctx.fillStyle = C.off;
    ctx.fillRect(0, 0, W, H);
    // Define image area with padding
    const imageScale = 0.8;
    const ps = (W * imageScale) / 40;
    const offsetX = (W - W * imageScale) / 2;
    const offsetY = (H - H * imageScale) / 2;
    // Subtle grid in image area
    ctx.globalAlpha = 0.04; ctx.strokeStyle = C.on; ctx.lineWidth = 0.5;
    const gs = ps;
    for (let i = 0; i <= 40; i++) {
      ctx.beginPath(); ctx.moveTo(offsetX + i*gs, offsetY); ctx.lineTo(offsetX + i*gs, offsetY + 40*gs); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(offsetX, offsetY + i*gs); ctx.lineTo(offsetX + 40*gs, offsetY + i*gs); ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // ─── Pixel Art with Animation ───
    if (pixels) {
      let drawPixels = true;
      const dynamicStyles = ['explode', 'melt', 'evaporate'];
      if (dynamicStyles.includes(animationStyle)) {
        drawPixels = false;
        if (!isStatic && animating) {
          particlesRef.current = particlesRef.current.map(p => ({
            ...p,
            x: p.x + (p.vx || 0),
            y: p.y + (p.vy || 0),
            life: p.life + (p.vlife || -0.01)
          })).filter(p => p.life > 0);
        }
        particlesRef.current.forEach((p, idx) => {
          const px = offsetX + p.x * ps, py = offsetY + p.y * ps;
          ctx.globalAlpha = animationStyle === 'evaporate' ? p.life : 1;
          ctx.fillStyle = colorSplash ? COLORS[idx % COLORS.length] : C.on;
          ctx.fillRect(px, py, ps, ps);
          if (colorSplash) {
            ctx.fillStyle = ctx.fillStyle + '80';
            ctx.fillRect(px + 2, py + 2, ps, ps);
          }
        });
        ctx.globalAlpha = 1;
        if (colorSplash) {
          ctx.save();
          ctx.globalAlpha = 0.3;
          ctx.fillStyle = '#000000';
          ctx.shadowColor = '#000000';
          ctx.shadowBlur = 10;
          ctx.shadowOffsetX = 5;
          ctx.shadowOffsetY = 5;
          particlesRef.current.forEach(p => {
            ctx.fillRect(offsetX + p.x * ps + 5, offsetY + p.y * ps + 5, ps, ps);
          });
          ctx.restore();
        }
      } else if (animationStyle === 'splash') {
        drawPixels = false;
        for (let y = 0; y < 40; y++) {
          for (let x = 0; x < 40; x++) {
            if (pixels[y][x]) {
              const wave = Math.sin(t + x * 0.2 + y * 0.3) * 2;
              const ox = offsetX + x * ps + wave;
              const oy = offsetY + y * ps + wave * 0.5;
              ctx.fillStyle = colorSplash ? COLORS[(x + y) % COLORS.length] : C.on;
              ctx.fillRect(ox, oy, ps, ps);
              if (colorSplash) {
                ctx.fillStyle = ctx.fillStyle + '80';
                ctx.fillRect(ox + 2, oy + 2, ps, ps);
              }
            }
          }
        }
        if (colorSplash) {
          ctx.save();
          ctx.globalAlpha = 0.3;
          ctx.fillStyle = '#000000';
          ctx.shadowColor = '#000000';
          ctx.shadowBlur = 10;
          ctx.shadowOffsetX = 5;
          ctx.shadowOffsetY = 5;
          for (let y = 0; y < 40; y++) {
            for (let x = 0; x < 40; x++) {
              if (pixels[y][x]) {
                const wave = Math.sin(t + x * 0.2 + y * 0.3) * 2;
                ctx.fillRect(offsetX + x * ps + wave + 5, offsetY + y * ps + wave * 0.5 + 5, ps, ps);
              }
            }
          }
          ctx.restore();
        }
      } else if (animationStyle === 'twist') {
        drawPixels = false;
        const cx = 20, cy = 20;
        for (let y = 0; y < 40; y++) {
          for (let x = 0; x < 40; x++) {
            if (pixels[y][x]) {
              const dx = x - cx, dy = y - cy;
              const angle = Math.atan2(dy, dx) + t * 0.1;
              const dist = Math.sqrt(dx*dx + dy*dy);
              const nx = cx + Math.cos(angle) * dist;
              const ny = cy + Math.sin(angle) * dist;
              ctx.fillStyle = colorSplash ? COLORS[(x + y) % COLORS.length] : C.on;
              ctx.fillRect(offsetX + nx * ps, offsetY + ny * ps, ps, ps);
              if (colorSplash) {
                ctx.fillStyle = ctx.fillStyle + '80';
                ctx.fillRect(offsetX + nx * ps + 2, offsetY + ny * ps + 2, ps, ps);
              }
            }
          }
        }
        if (colorSplash) {
          ctx.save();
          ctx.globalAlpha = 0.3;
          ctx.fillStyle = '#000000';
          ctx.shadowColor = '#000000';
          ctx.shadowBlur = 10;
          ctx.shadowOffsetX = 5;
          ctx.shadowOffsetY = 5;
          for (let y = 0; y < 40; y++) {
            for (let x = 0; x < 40; x++) {
              if (pixels[y][x]) {
                const dx = x - cx, dy = y - cy;
                const angle = Math.atan2(dy, dx) + t * 0.1;
                const dist = Math.sqrt(dx*dx + dy*dy);
                const nx = cx + Math.cos(angle) * dist;
                const ny = cy + Math.sin(angle) * dist;
                ctx.fillRect(offsetX + nx * ps + 5, offsetY + ny * ps + 5, ps, ps);
              }
            }
          }
          ctx.restore();
        }
      } else if (animationStyle === 'bounce') {
        drawPixels = false;
        for (let y = 0; y < 40; y++) {
          for (let x = 0; x < 40; x++) {
            if (pixels[y][x]) {
              const bounce = Math.abs(Math.sin(t + x * 0.1 + y * 0.1)) * 2;
              ctx.fillStyle = colorSplash ? COLORS[(x + y) % COLORS.length] : C.on;
              ctx.fillRect(offsetX + x * ps, offsetY + y * ps - bounce, ps, ps);
              if (colorSplash) {
                ctx.fillStyle = ctx.fillStyle + '80';
                ctx.fillRect(offsetX + x * ps + 2, offsetY + y * ps - bounce + 2, ps, ps);
              }
            }
          }
        }
        if (colorSplash) {
          ctx.save();
          ctx.globalAlpha = 0.3;
          ctx.fillStyle = '#000000';
          ctx.shadowColor = '#000000';
          ctx.shadowBlur = 10;
          ctx.shadowOffsetX = 5;
          ctx.shadowOffsetY = 5;
          for (let y = 0; y < 40; y++) {
            for (let x = 0; x < 40; x++) {
              if (pixels[y][x]) {
                const bounce = Math.abs(Math.sin(t + x * 0.1 + y * 0.1)) * 2;
                ctx.fillRect(offsetX + x * ps + 5, offsetY + y * ps - bounce + 5, ps, ps);
              }
            }
          }
          ctx.restore();
        }
      } else if (drawPixels) {
        for (let y = 0; y < 40; y++) {
          for (let x = 0; x < 40; x++) {
            if (!pixels[y][x]) continue;
            const px = offsetX + x * ps, py = offsetY + y * ps;
            let scale = 1;
            let bright = 48;
            if (!isStatic && animating) {
              const dx = x - 20, dy = y - 20;
              const dist = Math.sqrt(dx*dx + dy*dy);
              let ripple = 0, breathe = 0;
              if (animationStyle === 'ripple') {
                ripple = Math.sin(dist * 0.6 - t * 2.5) * 0.5 + 0.5;
                breathe = Math.sin(t * 0.8) * 0.08;
              } else if (animationStyle === 'breathe') {
                breathe = Math.sin(t * 0.8) * 0.12;
              }
              scale = 0.75 + ripple * 0.12 + breathe;
              bright = 48 + Math.floor(ripple * 28);
            }
            const s = ps * scale;
            const ox = px + (ps - s) / 2, oy = py + (ps - s) / 2;
            ctx.fillStyle = colorSplash ? COLORS[(x + y) % COLORS.length] : `rgb(${bright},${bright+1},${bright+3})`;
            ctx.fillRect(Math.round(ox), Math.round(oy), Math.ceil(s), Math.ceil(s));
            if (colorSplash) {
              ctx.fillStyle = ctx.fillStyle + '80';
              ctx.fillRect(Math.round(ox) + 2, Math.round(oy) + 2, Math.ceil(s), Math.ceil(s));
            }
          }
        }
        if (colorSplash) {
          ctx.save();
          ctx.globalAlpha = 0.3;
          ctx.fillStyle = '#000000';
          ctx.shadowColor = '#000000';
          ctx.shadowBlur = 10;
          ctx.shadowOffsetX = 5;
          ctx.shadowOffsetY = 5;
          for (let y = 0; y < 40; y++) {
            for (let x = 0; x < 40; x++) {
              if (pixels[y][x]) {
                const px = offsetX + x * ps, py = offsetY + y * ps;
                let scale = 1;
                if (!isStatic && animating) {
                  const dx = x - 20, dy = y - 20;
                  const dist = Math.sqrt(dx*dx + dy*dy);
                  let ripple = 0, breathe = 0;
                  if (animationStyle === 'ripple') {
                    ripple = Math.sin(dist * 0.6 - t * 2.5) * 0.5 + 0.5;
                    breathe = Math.sin(t * 0.8) * 0.08;
                  } else if (animationStyle === 'breathe') {
                    breathe = Math.sin(t * 0.8) * 0.12;
                  }
                  scale = 0.75 + ripple * 0.12 + breathe;
                }
                const s = ps * scale;
                const ox = px + (ps - s) / 2, oy = py + (ps - s) / 2;
                ctx.fillRect(Math.round(ox) + 5, Math.round(oy) + 5, Math.ceil(s), Math.ceil(s));
              }
            }
          }
          ctx.restore();
        }
      }
    }

    // ─── Harmonic Lissajous Patterns from Block Hash ─── (Kept but disabled by default)
    if (showHarmonics && harmonicParams && !isStatic) {
      const { freqA, freqB, phase, amplitude, rotation, complexity } = harmonicParams;
      const cx = W / 2, cy = H / 2, maxR = W * amplitude * imageScale;
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(rotation + t * 0.08);
      for (let layer = 0; layer < complexity; layer++) {
        const lo = layer * 0.7;
        ctx.globalAlpha = Math.max(0.025, 0.1 - layer * 0.02);
        ctx.strokeStyle = C.on; ctx.lineWidth = 1.2 - layer * 0.2;
        ctx.beginPath();
        const fA = freqA + layer * 0.5, fB = freqB + layer * 0.3;
        for (let i = 0; i <= 500; i++) {
          const p = (i / 500) * Math.PI * 2 * (3 + layer);
          const tO = t * (1 + layer * 0.15) + lo;
          const r = maxR * (0.55 + 0.45 * Math.sin(p * 2 + tO));
          const lx = r * Math.sin(fA * p + phase + tO * 0.5) * Math.exp(-i * 0.0008);
          const ly = r * Math.cos(fB * p + tO * 0.3) * Math.exp(-i * 0.0008);
          i === 0 ? ctx.moveTo(lx, ly) : ctx.lineTo(lx, ly);
        }
        ctx.stroke();
      }
      ctx.restore(); ctx.globalAlpha = 1;
    }

    // ─── Meme Text ───
    const drawText = (text, y, maxW) => {
      if (!text) return;
      let fs = Math.floor(W * 0.055);
      ctx.font = `bold ${fs}px 'Robotastic Regular', 'Courier New', 'Courier', monospace`;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      while (ctx.measureText(text).width > maxW && fs > 12) {
        fs -= 2; ctx.font = `bold ${fs}px 'Robotastic Regular', 'Courier New', 'Courier', monospace`;
      }
      const words = text.split(" "); const lines = []; let cur = "";
      for (const w of words) {
        const test = cur ? `${cur} ${w}` : w;
        if (ctx.measureText(test).width > maxW) { if (cur) lines.push(cur); cur = w; } else cur = test;
      }
      if (cur) lines.push(cur);
      const lh = fs * 1.2;
      const startY = y - (lines.length * lh) / 2 + lh / 2;
      lines.forEach((line, i) => {
        const ly = startY + i * lh;
        ctx.strokeStyle = C.off; ctx.lineWidth = Math.max(5, fs * 0.14); ctx.lineJoin = "round";
        ctx.strokeText(line, W / 2, ly);
        ctx.fillStyle = C.on; ctx.fillText(line, W / 2, ly);
      });
    };
    if (memeText.top) drawText(memeText.top, W * 0.06, W - 40);
    if (memeText.bottom) drawText(memeText.bottom, H - W * 0.06, W - 40);

    // Floor price
    if (floorPrice !== null) {
      ctx.font = `bold ${Math.floor(W*0.017)}px 'Courier New', monospace`;
      ctx.textAlign = "right"; ctx.fillStyle = C.mid;
      ctx.fillText(`FLOOR: ${floorPrice.toFixed(4)} ETH${priceChange !== null ? ` (${priceChange >= 0?"+":""}${priceChange.toFixed(1)}%)` : ""}`, W - 12, H - 12);
    }
    if (showWatermark) {
      ctx.font = `bold ${Math.floor(W*0.015)}px 'Courier New', monospace`;
      ctx.textAlign = "left"; ctx.fillStyle = C.mid;
      ctx.fillText(`@normiesArt · #${normieId}${pixelCount ? ` · ${pixelCount}px` : ""}`, 12, H - 12);
    }
    if (blockHash && showHarmonics && !isStatic) {
      ctx.font = `${Math.floor(W*0.011)}px 'Courier New', monospace`;
      ctx.textAlign = "center"; ctx.fillStyle = C.faint;
      ctx.fillText(`0x${blockHash.substring(0, 16)}...`, W / 2, H - 12);
    }
  }, [pixels, memeText, floorPrice, priceChange, showWatermark, normieId, pixelCount, blockHash, showHarmonics, harmonicParams, animating, animSpeed, animationStyle, colorSplash]);

  // ─── Animation Loop ───
  useEffect(() => {
    if (!animating || !canvasRef.current || !pixels) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = 800; canvas.height = 800;
    let running = true;
    const loop = () => {
      if (!running) return;
      drawFrame(ctx, 800, 800, frameRef.current, false);
      frameRef.current++;
      animRef.current = requestAnimationFrame(loop);
    };
    loop();
    return () => { running = false; if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [animating, pixels, drawFrame]);

  // Static when paused
  useEffect(() => {
    if (animating || !pixels || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = 800; canvas.height = 800;
    drawFrame(canvas.getContext("2d"), 800, 800, 0, true);
  }, [animating, pixels, drawFrame]);

  // ─── GIF Export ───
  const exportGIF = async () => {
    if (!pixels) return;
    setGifProgress(0);
    const W = 320, H = 320;
    const off = document.createElement("canvas");
    off.width = W; off.height = H;
    const offCtx = off.getContext("2d");
    const enc = new GIFEncoder(W, H);
    enc.setDelay(80);
    const total = 36;
    for (let i = 0; i < total; i++) {
      drawFrame(offCtx, W, H, i * 2, false);
      enc.addFrame(offCtx);
      setGifProgress(Math.round(((i+1)/total)*100));
      await new Promise(r => setTimeout(r, 5));
    }
    const data = enc.encode();
    if (data) {
      const blob = new Blob([data], { type: "image/gif" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `normie-${normieId}-animated.gif`;
      a.click();
    }
    setGifProgress(null);
  };

  const downloadPNG = () => {
    const c = document.createElement("canvas");
    c.width = 800; c.height = 800;
    drawFrame(c.getContext("2d"), 800, 800, frameRef.current, true);
    const a = document.createElement("a"); a.download = `normie-${normieId}-meme.png`;
    a.href = c.toDataURL("image/png"); a.click();
  };

  const copyImg = async () => {
    try {
      const c = document.createElement("canvas");
      c.width = 800; c.height = 800;
      drawFrame(c.getContext("2d"), 800, 800, frameRef.current, true);
      const blob = await new Promise(r => c.toBlob(r, "image/png"));
      await navigator.clipboard.write([new ClipboardItem({"image/png": blob})]);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch(e) { alert("Copy failed"); }
  };

  const sd = { pump: { e: "\u{1F680}", l: "PUMP", c: "#3a7d53" }, dump: { e: "\u{1F4C9}", l: "DUMP", c: "#a0382e" }, crab: { e: "\u{1F980}", l: "CRAB", c: "#9a7a20" }, generic: { e: "\u{26A1}", l: "VIBE", c: C.on } }[sentiment];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.on, fontFamily: "'SF Mono','Menlo','Courier New',monospace" }}>
      {/* ══════ HEADER ══════ */}
      <header style={{ padding: "14px 20px", borderBottom: `1px solid ${C.divider}`, background: C.white }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, background: C.on, borderRadius: 5, display: "grid", placeItems: "center" }}>
              <div style={{ width: 14, height: 14, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0.5 }}>
                {[1,0,0,1,0,1,1,0,0,1,1,0,1,0,0,1].map((v,i)=>(<div key={i} style={{ background: v?C.off:"transparent" }}/>))}
              </div>
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 15, fontWeight: 800, letterSpacing: "-0.3px" }}>NORMIES</h1>
              <p style={{ margin: 0, fontSize: 8, color: C.mid, letterSpacing: "2px", fontWeight: 600 }}>MEME GENERATOR · ON-CHAIN</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.off, borderRadius: 6, padding: "5px 12px" }}>
            {floorPrice !== null ? (<>
              <span style={{ fontSize: 8, color: C.mid, fontWeight: 700, letterSpacing: "1px" }}>FLOOR</span>
              <span style={{ fontSize: 13, fontWeight: 800 }}>{floorPrice.toFixed(4)} Ξ</span>
              {priceChange !== null && <span style={{ fontSize: 9, fontWeight: 700, color: priceChange>=0?"#3a7d53":"#a0382e", background: priceChange>=0?"#3a7d5315":"#a0382e15", padding: "1px 5px", borderRadius: 3 }}>{priceChange>=0?"+":""}{priceChange.toFixed(1)}%</span>}
              <span style={{ fontSize: 8, fontWeight: 700, color: sd.c, background: sd.c+"15", padding: "1px 5px", borderRadius: 3 }}>{sd.e} {sd.l}</span>
            </>) : <span style={{ fontSize: 9, color: C.mid }}>SET FLOOR →</span>}
          </div>
        </div>
      </header>

      {/* ══════ MAIN ══════ */}
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 240px", gap: 16 }}>
        {/* Canvas */}
        <div>
          <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.divider}`, overflow: "hidden", position: "relative" }}>
            {!pixels && !loading ? (
              <div style={{ height: 460, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
                <div style={{ width: 48, height: 48, border: `2px solid ${C.faint}`, borderRadius: 8, display: "grid", placeItems: "center" }}>
                  <span style={{ fontSize: 22, color: C.faint }}>?</span>
                </div>
                <p style={{ color: C.mid, fontSize: 11, textAlign: "center", lineHeight: 1.7 }}>Generate a random Normie or<br/>enter a token ID (0–9999)</p>
              </div>
            ) : loading ? (
              <div style={{ height: 460, display: "grid", placeItems: "center" }}>
                <span style={{ fontSize: 11, color: C.mid }}>Loading Normie #{normieId}...</span>
              </div>
            ) : (
              <canvas ref={canvasRef} style={{ width: "100%", display: "block" }} />
            )}
            {pixelCount > 0 && <div style={{ position: "absolute", top: 10, right: 10, background: C.on, color: C.off, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 3, letterSpacing: "0.5px" }}>{pixelCount}px</div>}
          </div>
          {/* Input + Generate */}
          <div style={{ display: "flex", gap: 5, marginTop: 8 }}>
            <input type="number" min="0" max="9999" placeholder="#ID" value={idInput} onChange={e=>setIdInput(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&idInput)loadNormie(parseInt(idInput));}}
              style={{ ...inp, width: 72 }} />
            <Btn primary onClick={()=>idInput?loadNormie(parseInt(idInput)):loadNormie()} disabled={loading} style={{ flex: 1 }}>
              {loading?"Loading...":pixels?"New Normie":"Generate"}
            </Btn>
            {pixels && <>
              <Btn onClick={shuffleMeme}>Shuffle</Btn>
              <Btn onClick={()=>setAnimating(a=>!a)}>{animating?"Pause":"Play"}</Btn>
            </>}
          </div>
          {/* Export */}
          {pixels && (
            <div style={{ display: "flex", gap: 5, marginTop: 5 }}>
              <Btn onClick={downloadPNG} style={{ flex: 1 }}>PNG</Btn>
              <Btn onClick={exportGIF} disabled={gifProgress!==null} style={{ flex: 1 }}>
                {gifProgress!==null?`GIF ${gifProgress}%`:"GIF"}
              </Btn>
              <Btn onClick={copyImg}>{copied?"Copied!":"Copy"}</Btn>
            </div>
          )}
          {gifProgress!==null && (
            <div style={{ marginTop: 4, height: 2, background: C.faint, borderRadius: 1, overflow: "hidden" }}>
              <div style={{ width: `${gifProgress}%`, height: "100%", background: C.on, transition: "width 0.15s" }} />
            </div>
          )}
        </div>

        {/* ═══ Controls ═══ */}
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          <div style={{display:"flex",gap:3,marginBottom:7}}>
            {['basic', 'advanced', 'traits'].map(t=>(<Btn key={t} onClick={()=>setActiveTab(t)} active={activeTab===t} style={{flex:1}}>{t.toUpperCase()}</Btn>))}
          </div>
          {activeTab === 'basic' && <>
            <P t="TEXT">
              <input type="text" placeholder="Top text..." value={customTop} onChange={e=>setCustomTop(e.target.value)} style={inp}/>
              <input type="text" placeholder="Bottom text..." value={customBottom} onChange={e=>setCustomBottom(e.target.value)} style={{...inp,marginTop:4}}/>
              <Btn onClick={applyCustom} style={{width:"100%",marginTop:5}}>Apply</Btn>
            </P>
            <P t="MOOD">
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3}}>
                {(["pump","dump","crab","generic"]).map(s=>{
                  const d={pump:{e:"\u{1F680}"},dump:{e:"\u{1F4C9}"},crab:{e:"\u{1F980}"},generic:{e:"\u{26A1}"}}[s];
                  return <Btn key={s} onClick={()=>setSentiment(s)} active={sentiment===s}>{d.e} {s}</Btn>;
                })}
              </div>
            </P>
            <P t="FLOOR PRICE">
              <div style={{display:"flex",gap:3}}>
                <input type="number" step="0.001" placeholder="ETH" value={manualPrice} onChange={e=>setManualPrice(e.target.value)} style={{...inp,flex:1}}/>
                <input type="number" step="0.1" placeholder="%Δ" value={manualChange} onChange={e=>setManualChange(e.target.value)} style={{...inp,width:44}}/>
                <Btn onClick={applyManualPrice}>Set</Btn>
              </div>
            </P>
            <label style={{display:"flex",alignItems:"center",gap:5,padding:"7px 10px",background:C.white,border:`1px solid ${C.divider}`,borderRadius:6,fontSize:9,color:C.mid,cursor:"pointer"}}>
              <input type="checkbox" checked={showWatermark} onChange={e=>setShowWatermark(e.target.checked)} style={{accentColor:C.on}}/> @normiesArt watermark
            </label>
          </>}
          {activeTab === 'advanced' && <>
            <P t="BLOCK HARMONICS">
              <label style={{ display:"flex",alignItems:"center",gap:5,fontSize:9,color:C.mid,cursor:"pointer",marginBottom:5 }}>
                <input type="checkbox" checked={showHarmonics} onChange={e=>setShowHarmonics(e.target.checked)} style={{accentColor:C.on}}/> Harmonic overlay
              </label>
              {harmonicParams && (
                <div style={{ fontSize: 9, color: C.mid, lineHeight: 2 }}>
                  {[["Freq",`${harmonicParams.freqA}:${harmonicParams.freqB}`],["Phase",`${(harmonicParams.phase/Math.PI).toFixed(2)}π`],["Layers",harmonicParams.complexity]].map(([k,v])=>(
                    <div key={k} style={{display:"flex",justifyContent:"space-between"}}><span>{k}</span><span style={{color:C.on,fontWeight:600}}>{v}</span></div>
                  ))}
                </div>
              )}
              <Btn onClick={generateBlockHash} style={{width:"100%",marginTop:5}}>New Block</Btn>
              {blockHash&&<p style={{margin:"4px 0 0",fontSize:7,color:C.faint,wordBreak:"break-all",lineHeight:1.5}}>0x{blockHash.substring(0,32)}...</p>}
            </P>
            <P t="SPEED">
              <div style={{display:"flex",gap:3}}>
                {[0.5,1,2,3].map(s=>(<Btn key={s} onClick={()=>setAnimSpeed(s)} active={animSpeed===s} style={{flex:1}}>{s}x</Btn>))}
              </div>
            </P>
            <P t="PIXEL ANIMATION">
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:3}}>
                {['none', 'breathe', 'ripple', 'evaporate', 'melt', 'explode', 'splash', 'twist', 'bounce'].map(s=>(<Btn key={s} onClick={()=>setAnimationStyle(s)} active={animationStyle===s}>{s.toUpperCase()}</Btn>))}
              </div>
            </P>
            <P t="COLOR SPLASH">
              <Btn onClick={()=>setColorSplash(m=>!m)} active={colorSplash}>COLOR SPLASH</Btn>
            </P>
          </>}
          {activeTab === 'traits' && traits && (
            <P t={`#${normieId} TRAITS`}>
              {traits.map((t,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:9,padding:"1px 0",color:C.mid}}>
                  <span>{t.trait_type}</span><span style={{color:C.on,fontWeight:600}}>{t.value}</span>
                </div>
              ))}
            </P>
          )}
        </div>
      </div>

      <footer style={{padding:"14px 20px",borderTop:`1px solid ${C.divider}`,textAlign:"center",marginTop:16}}>
        <p style={{margin:0,fontSize:8,color:C.faint,letterSpacing:"1.5px"}}>NORMIES · ON-CHAIN GENERATIVE FACES · BUILT BY SERC & YIGIT · 40×40 · 10,000</p>
      </footer>
      <style>{`
        @media(max-width:700px){div[style*="grid-template-columns: 1fr 240px"]{grid-template-columns:1fr!important;}}
        input:focus{outline:none;border-color:${C.mid}!important;}
        button:active{transform:scale(0.97);}
      `}</style>
    </div>
  );
}

// ─── Shared ───
const inp = {
  padding:"6px 9px",background:"#fff",color:"#48494b",
  border:"1px solid #c8c9cb",borderRadius:5,
  fontFamily:"'SF Mono','Menlo','Courier New',monospace",
  fontSize:10,boxSizing:"border-box",width:"100%",
};
function P({t,children}){
  return(<div style={{background:"#fff",borderRadius:7,border:"1px solid #d5d6d8",padding:10}}>
    <h3 style={{margin:"0 0 7px",fontSize:8,color:"#8e8f91",letterSpacing:"1.5px",fontWeight:700}}>{t}</h3>{children}
  </div>);
}
function Btn({children,onClick,disabled,active,primary,style={}}){
  return(<button onClick={onClick} disabled={disabled} style={{
    padding:"6px 10px",
    background:primary?"#48494b":active?"#48494b10":"#fff",
    color:primary?"#e3e5e4":active?"#48494b":"#48494b",
    border:`1px solid ${active?"#48494b44":"#c8c9cb"}`,
    borderRadius:5,fontFamily:"'SF Mono','Menlo','Courier New',monospace",
    fontSize:9,fontWeight:700,cursor:disabled?"not-allowed":"pointer",
    textTransform:"uppercase",letterSpacing:"0.3px",
    opacity:disabled?0.35:1,transition:"all 0.1s",...style
  }}>{children}</button>);
}